import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../shared/models/apiurl.models";
import {LoadingMaskServiceService} from "../../../components/loading-frame/loading-frame.component";
import {QuestionBase} from "../models/question-base";
import {adsRoute} from "../../../app.models";

@Injectable()
export class AdvFormService {
  private subject = new Subject<any>();
  private qObserver = new Subject<any>();
  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  }
  QuestionMap = new Map();

  /**
   * SCRUM-239: set once the user approves the תקנון anywhere in this publish
   * session (contact step or the plan picker). The contact step re-reads the
   * user from the server whenever a dialog closes and used to overwrite the
   * form's acceptTerms with a stale `false`, so the plans stayed locked even
   * right after approving.
   */
  termsAcceptedLocally = false;

  /**
   * The contact step counts as done only when the phone number is filled in
   * AND verified AND the תקנון is approved. Until then the plan picker
   * ("בחירת מסלול") stays closed and disabled.
   */
  isContactComplete(contact: any = this.getQuestion('userContactDetails')): boolean {
    if (!contact) return false;
    const digits = String(contact.phoneNumber ?? '').replace(/\D/g, '');
    const phoneFilled = digits.length >= 9;
    const phoneVerified = contact.validPhone === true;
    const termsAccepted = !!contact.acceptTerms || this.termsAcceptedLocally;
    return phoneFilled && phoneVerified && termsAccepted;
  }
  /**
   * The uploaded video (preview + upload meta) of this publish session, kept
   * outside QuestionMap (not sent to the server) so the video step shows it
   * again when the user comes back to it. Its `session_id` is the answer.
   */
  uploadedVideo: any = null;

  public adsRoutes: adsRoute[];
  private activeQuestionKey: string | null = null;
  constructor(private http: HttpClient, private apiKey:ApiUrl, private loadingMaskServiceService: LoadingMaskServiceService) {}

  addQuestion(index: string,answer: any, actionAfter=null){
    this.QuestionMap.set(index,answer)
    this.sendClickEvent()
    this.sendQOClickEvent({index:index, action:actionAfter})
  }

  deleteQuestion(index: string, actionAfter=null){
    this.QuestionMap.delete(index);
    this.sendClickEvent(index)
    this.sendQOClickEvent({index:index, action: actionAfter})
  }

  getQuestion(index: string) {
    return this.QuestionMap.get(index);
  }

  setActiveQuestion(questionKey: string | null): void {
    this.activeQuestionKey = questionKey;
  }

  getActiveQuestion(): string | null {
    return this.activeQuestionKey;
  }

  resetForm(): void {
    this.QuestionMap.clear();
    this.relatedByKey.clear();
    this.uploadedVideo = null;
    this.activeQuestionKey = null;
    this.adsRoutes = [];
    this.sendClickEvent({type: 'reset'});
    this.sendQOClickEvent({index: null, action: 'reset', reset: true});
  }

  loadAdsRouts(): void{
    this.http.get(this.apiKey.getClientAdsRouteUrl(), this.options).subscribe(res=>{
      this.adsRoutes = res['results'];
    })
  }

  getValues(data: any):any{
    return data
  }
  /**
   * @param extra fields sent with THIS request only, never stored in the
   *   form (SCRUM-313: `coupon_redemption_id` + `coupon_request_id`), so a
   *   later regular-plan publish can't carry a stale coupon.
   */
  publishData(extra: Record<string, any> | null = null): Observable<any>{
    var arrJson:any = {}
    this.QuestionMap.forEach((value,key) => arrJson[key]=this.getValues(value))
    if (extra) Object.assign(arrJson, extra)
    this.loadingMaskServiceService.setOnLoadingMask(true, true);

    return this.http.post<any>(this.apiKey.getFormsQuestionUrl(), JSON.stringify(arrJson), this.options)
  }

  sendQOClickEvent(obj: any = null){
    this.qObserver.next(obj);
  }

  getQOClickEvent():Observable<any>{
    return this.qObserver.asObservable();
  }

  sendClickEvent(obj: any = null){
    this.subject.next(obj);
  }

  getClickEvent():Observable<any>{
    return this.subject.asObservable();
  }

  /** Every question's `relatedField`, registered when a form is loaded / swapped. */
  private relatedByKey = new Map<string, string>();

  registerQuestions(questions: QuestionBase<any>[]): void {
    this.relatedByKey.clear();
    (questions || []).forEach(q => this.relatedByKey.set(q.key, q.relatedField || ''));
  }

  private splitKeys(raw: string | null | undefined): string[] {
    return String(raw || '').split(',').map(k => k.trim()).filter(k => k && k !== 'None');
  }

  /**
   * All keys that depend on [key], following the relatedField chain to the
   * end (producer -> model -> year -> description), not only the direct children.
   */
  dependentKeys(key: string, own?: string): string[] {
    const result: string[] = [];
    const seen = new Set<string>([key]);
    const queue = this.splitKeys(own ?? this.relatedByKey.get(key));
    while (queue.length) {
      const k = queue.shift()!;
      if (seen.has(k)) continue;
      seen.add(k);
      result.push(k);
      queue.push(...this.splitKeys(this.relatedByKey.get(k)));
    }
    return result;
  }

  /**
   * A parent answer changed / was cleared: delete every dependent answer.
   * deleteQuestion() also tells each child widget to reset what it shows.
   */
  clearRelatedFields(questionBase: QuestionBase<any>, source: string | null = null): void {
    for (const field of this.dependentKeys(questionBase.key, questionBase.relatedField)) {
      if (this.getQuestion(field) != null) {
        this.deleteQuestion(field);
      }
    }
    if (source == "clearSearch") {
      this.deleteQuestion(questionBase.key)
    }
  }

}
